<template>
  <div
    class="sales-contact-widget"
    :class="[
      `sales-contact-widget--${showSingleContact ? 'single' : 'select'}`,
    ]">
    <!-- Summary of currently stored data. -->
    <div class="sales-contact-widget__header">
      <p class="sales-contact-widget__title">
        {{ t.salesContactTitle }}
      </p>
      <template v-if="showSingleContact">
        <div
          v-if="transformedLocalStorageData.country"
          class="sales-contact-widget__meta">
          <span v-if="transformedLocalStorageData.country">
            {{
              `${t.meta.country}: ${transformedLocalStorageData.country.name}`
            }}
          </span>
          <span v-if="transformedLocalStorageData.crop">
            {{ `${t.meta.crop}: ${transformedLocalStorageData.crop.name}` }}
          </span>
          <span v-if="transformedLocalStorageData.zipCode">
            {{ `${t.meta.zipCode}: ${transformedLocalStorageData.zipCode}` }}
          </span>
        </div>
      </template>
      <template
        v-if="
          showSingleContact || salesContactMissing || preselectedCountryId > 0
        ">
        <button
          class="sales-contact-widget__configure"
          :title="t.change"
          @click="openModal">
          <i class="fa-solid fa-gear"></i>
          {{ t.change }}
        </button>
      </template>
    </div>

    <!-- Sales contact. -->
    <div class="sales-contact-widget__content">
      <template v-if="showSingleContact || salesContactMissing">
        <sales-contact-result
          :lang="lang"
          type="widget"
          :result="localSalesContact"
          :noResults="salesContactMissing"
          :noResultsText="noResultsText" />
      </template>
      <template v-else>
        <!-- Sales contact form. -->
        <transition name="sales-contact-finder-slide-in" mode="out-in" appear>
          <div
            class="sales-contact-widget__select sales-contact-finder__select">
            <SalesContactForm
              :lang="lang"
              show-only-country
              @modalRequested="openPrefilledModal"
              @updated="updateContact" />
          </div>
        </transition>
      </template>
      <Loading v-if="loading" />
      <!-- If country on single select has additional fields
        - open modal with all of them & preselect country. -->
      <DialogModal
        v-model="displayModal"
        modal-type="sales-contact-widget"
        ref="salesContactModal">
        <!-- Header. -->
        <template #header>
          <p class="sales-contact-widget__title">
            {{ t.findLocalSalesContact }}
          </p>
        </template>
        <!-- Content. -->
        <template #content>
          <SalesContactForm
            :lang="lang"
            :defaultCountryId="preselectedCountryId"
            :currentCountryId="preselectedCountryId"
            @updated="updateContact" />
        </template>
      </DialogModal>
    </div>
  </div>
</template>

<script>
// components
import DialogModal from '../elements/DialogModal';
import Loading from '../elements/Loading';
import SalesContactForm from './SalesContactForm';
import SalesContactResult from './SalesContactResult';
// utils
import translationStrings from '../../translations/translationStrings';
import { getSalesContact } from '../../utils/api';
import { getState } from '../../utils/persistedState';

export default {
  name: 'sales-contact-widget',
  components: {
    DialogModal,
    Loading,
    SalesContactForm,
    SalesContactResult,
  },
  props: {
    lang: {
      type: String,
      required: true,
    },
    placeholderImage: {
      type: String,
      required: true,
    },
    noResultsText: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      displayModal: false,
      loading: true,
      localSalesContact: {},
      localStorageData: null, // from local storage
      preselectedCountryId: 0,
      salesContactMissing: false,
      t: translationStrings,
    };
  },
  computed: {
    showSingleContact() {
      return !this.objectEmpty(this.localSalesContact);
    },
    // Based on stored IDs get the data that is actually displayed.
    transformedLocalStorageData() {
      return this.showSingleContact
        ? {
            country: this.localSalesContact.countries.find(
              (c) => c.id === this.localStorageData?.country,
            ),
            crop: this.localSalesContact.crops.find(
              (c) => c.id === this.localStorageData?.crop,
            ),
            zipCode: this.localStorageData?.zipCode,
          }
        : null;
    },
  },
  watch: {
    preselectedCountryId() {
      this.salesContactMissing = false;
    },
  },
  async mounted() {
    // Check if local storage holds a sales contact.
    await this.updateContact();
    this.loading = false;
    this.salesContactMissing = false;
  },
  methods: {
    async fetchSingleSalesContact(id) {
      try {
        const { data } = await getSalesContact(this.lang, id);
        this.localSalesContact = data;
      } catch ({ response }) {
        if (response) {
          console.error(response);
        }
      } finally {
        // Disable loading.
        this.loading = false;
      }
    },
    async updateContact() {
      // your always getting info from local storage,
      // bc it's added there as soon as you choose it?
      this.localStorageData = getState();
      if (this.localStorageData) {
        await this.fetchSingleSalesContact(this.localStorageData?.id);

        // Update default selection for modal that might be opened in future.
        this.preselectedCountryId = this.localStorageData.country;
      } else {
        // check for no results
        this.salesContactMissing = true;
      }

      // Close the modal in case it is open.
      if (this.displayModal) {
        this.$refs.salesContactModal.closeDialogModal();
        this.displayModal = false;
      }
    },
    openModal() {
      this.$refs.salesContactModal.openDialogModal();
      this.displayModal = true;
    },
    openPrefilledModal(payload) {
      this.preselectedCountryId = payload;
      this.$nextTick(() => {
        this.openModal();
      });
    },
    // @todo make this into utils or something
    objectEmpty(obj) {
      return Object.keys(obj).length === 0;
    },
  },
};
</script>
